import { GalleryService } from "features/gallery/service/gallery.service"
import {
  BuildThumbnailRepresentationFunction,
  ThumbnailRepresentation,
} from "features/gallery/service/thumbnail.service"
import { GalleryMedium } from "features/gallery/gallery.types"
import { mdiMinus, mdiPlus, mdiStar, mdiStarOutline } from "@mdi/js"
import { GalleryThumbnailAction } from "features/gallery/thumbnail/GalleryThumbnail.vue"

/**
 * Builds and Returns the Add-Button (Adding to Highlights) for a GalleryThumbnail.
 */
const buildAddToHighlightsAction = (
  galleryService: GalleryService,
  thumbnailRepresentation: ThumbnailRepresentation
): GalleryThumbnailAction => {
  // Flag to prevent multiple clicks/calls
  let isAdding = false

  return {
    icon: mdiStarOutline,
    tooltip: "Zu den Highlights hinzufügen",
    action: async () => {
      if (isAdding) return
      isAdding = true
      const highlight = await galleryService.addGalleryToHighlight(
        thumbnailRepresentation.galleryMedium.id
      )
      if (highlight) {
        thumbnailRepresentation.galleryMedium.highlight_id = highlight.id
      }
      isAdding = false
    },
  }
}

/**
 * Builds and Returns the Remove-Button (Removing from Highlights) for a GalleryThumbnail.
 */
const buildRemoveFromHighlightsButton = (
  galleryService: GalleryService,
  galleryMedium: GalleryMedium
): GalleryThumbnailAction => ({
  icon: mdiStar,
  showAlways: true,
  tooltip: "Aus den Highlights entfernen",
  action: () => galleryService.removeGalleryFromHighlight(galleryMedium),
})

/**
 * Builds the ThumbnailRepresentation for a GalleryThumbnail. If user has no edit rights, the top-left action is removed.
 * Else either the Add-Button or the Remove-Button is added to the top-left action.
 */
export const buildThumbnailRepresentation =
  (galleryService: GalleryService): BuildThumbnailRepresentationFunction =>
  (thumbnailRepresentation, canEdit) => {
    if (!canEdit) {
      thumbnailRepresentation.topLeftAction.value = null
      return thumbnailRepresentation
    } else {
      thumbnailRepresentation.topLeftAction.value = !thumbnailRepresentation
        .isHighlight.value
        ? buildAddToHighlightsAction(galleryService, thumbnailRepresentation)
        : buildRemoveFromHighlightsButton(
            galleryService,
            thumbnailRepresentation.galleryMedium
          )
      return thumbnailRepresentation
    }
  }
